import React from 'react';
import { cn } from '@/utils/cn';

interface AutocompleteTriggerProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClear'> {
  selected: T | null;
  placeholder: string;
  disabled?: boolean;
  isOpenList: boolean;
  renderSelectedItem?: (option: T) => React.ReactNode;
  getLabel: (option: T) => string;
  onClear: (e: React.MouseEvent) => void;
}

const AutocompleteTriggerInner = <T,>(props: AutocompleteTriggerProps<T>, ref: React.ForwardedRef<HTMLDivElement>) => {
  const {
    selected,
    placeholder,
    disabled = false,
    isOpenList,
    renderSelectedItem,
    getLabel,
    onClear,
    className,
    ...rest
  } = props;

  const isHasSelectedItems = selected !== null;

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-between w-full pl-[12px] pr-[28px] h-[38px] bg-white rounded-[8px] border border-[#D9D9D9] cursor-pointer text-left text-[14px] font-normal relative',
        {
          'border-[#0C8DBF]': isOpenList,
          'opacity-50 cursor-not-allowed': disabled,
          'hover:border-[#0C8DBF]': !disabled,
        },
        className
      )}
      {...rest}
    >
      <div className="flex-1 truncate">
        {selected ? (
          renderSelectedItem ? (
            renderSelectedItem(selected)
          ) : (
            <span className="truncate">{getLabel(selected)}</span>
          )
        ) : (
          <span className="text-[#9D9D9C] truncate">{placeholder}</span>
        )}
      </div>

      <div className="h-fit w-fit flex gap-[4px] items-center justify-center absolute right-[10px] top-1/2 -translate-y-1/2">
        {isHasSelectedItems && !disabled ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear(e);
            }}
            className="p-[2px] cursor-pointer hover:bg-[#CC0202]/10 rounded"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 14 14">
              <path d="M10.5 3.5L3.5 10.5" stroke="#CC0202" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3.5 3.5L10.5 10.5" stroke="#CC0202" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}
        <svg
          className={cn('w-4 h-4  fill-[#9D9D9C] transition-transform duration-300', {
            'rotate-180': isOpenList,
          })}
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path d="M10.0151 4.76531L6.26512 8.51531C6.23029 8.55018 6.18893 8.57784 6.14341 8.59671C6.09789 8.61558 6.04909 8.62529 5.99981 8.62529C5.95053 8.62529 5.90173 8.61558 5.8562 8.59671C5.81068 8.57784 5.76932 8.55018 5.73449 8.51531L1.98449 4.76531C1.93199 4.71287 1.89623 4.64602 1.88173 4.57324C1.86724 4.50046 1.87467 4.42501 1.90307 4.35645C1.93148 4.28789 1.97959 4.22931 2.04131 4.1881C2.10304 4.1469 2.1756 4.12494 2.24981 4.125H9.74981C9.82402 4.12494 9.89658 4.1469 9.9583 4.1881C10.02 4.22931 10.0681 4.28789 10.0965 4.35645C10.1249 4.42501 10.1324 4.50046 10.1179 4.57324C10.1034 4.64602 10.0676 4.71287 10.0151 4.76531Z" />
        </svg>
      </div>
    </div>
  );
};

const AutocompleteTrigger = React.forwardRef(AutocompleteTriggerInner) as <T>(
  props: AutocompleteTriggerProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;

export default AutocompleteTrigger;
